import { NodeType, type CanvasNode, type HistoryItem, type LightHistoryItem, type ReferenceTag } from '../types';
import { inferReferenceKind } from './referenceMedia';
import { isLocalMediaUrl } from './desktopFolder';

/**
 * The workspace's Edit page keeps its timeline in one node with this id. It is
 * saved and synced like any other node, so the edit travels with the workspace,
 * but it is never drawn on the canvas.
 */
export const WORKSPACE_EDIT_NODE_ID = 'workspace-edit';

export const isWorkspaceEditNode = (node: Pick<CanvasNode, 'id'>): boolean => node.id === WORKSPACE_EDIT_NODE_ID;

/** One piece of workspace media offered to the Edit page. */
export interface WorkspaceMediaItem {
  id: string;
  /** Identifies the media itself, so the same file is only offered (and loaded) once. */
  sourceId: string;
  url: string;
  label: string;
  /** A short remark shown under the label. */
  note?: string;
}

const hash = (text: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
};

/**
 * A key that stays the same for a stored file across sessions, or null when the
 * URL only lives for this session (blob and data URLs).
 */
export const stableMediaKey = (url: string): string | null => {
  const file = /^file:\/\/([^/?#]+)/i.exec(url);
  if (file) return `f-${file[1]}`;
  if (!/^https?:\/\//i.test(url)) return null;
  // The desktop app's address of a local file only works while this run of the app lasts
  if (isLocalMediaUrl(url)) return null;
  // A Firebase download URL names the file in its path; the token in the query can change.
  const isFirebase = /firebasestorage\.(googleapis\.com|app)/i.test(url);
  return `u-${hash(isFirebase ? url.split(/[?#]/)[0] : url)}`;
};

const videoLabel = (title?: string, prompt?: string): string =>
  title && title !== 'Video' ? title : (prompt?.trim().slice(0, 40) || 'Video');

export const isReferenceNode = (node: Pick<CanvasNode, 'type'>) => node.type === NodeType.MEDIA_REFERENCE || node.type === NodeType.AUDIO_REFERENCE;

/**
 * Every video of the workspace: what the Video and Reference nodes hold now,
 * followed by earlier takes that only the generation history still knows.
 */
export const collectWorkspaceVideos = (
  nodes: CanvasNode[],
  history: (HistoryItem | LightHistoryItem)[] = [],
): WorkspaceMediaItem[] => {
  const items: WorkspaceMediaItem[] = [];
  const seen = new Set<string>();
  const add = (key: string, url: string, label: string, note?: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ id: key, sourceId: key, url, label, ...(note ? { note } : {}) });
  };

  for (const node of nodes) {
    const url = node.data.videoUri;
    if (!url) continue;
    const key = stableMediaKey(url) ?? `node-${node.id}`;
    if (node.type === NodeType.VIDEO_GENERATOR) add(key, url, videoLabel(node.data.title, node.data.prompt));
    else if (isReferenceNode(node)) add(key, url, node.data.videoFileName || node.data.title || 'Reference clip', 'Reference');
  }

  for (const item of history) {
    if (item.type !== NodeType.VIDEO_GENERATOR) continue;
    const full = 'data' in item ? item.data : undefined;
    const url = full ? full.videoUri : (item as LightHistoryItem).videoRef;
    // A take that is no longer on a node can only be loaded from a stored file.
    const key = url ? stableMediaKey(url) : null;
    if (!url || !key) continue;
    const light = item as LightHistoryItem;
    add(key, url, videoLabel(full ? full.title : light.title, full ? full.prompt : light.prompt), 'Earlier take');
  }
  return items;
};

export type ReferenceChoiceKind = 'image' | 'video' | 'audio';

/** One piece of workspace media a Reference node can hold, with what is known about it. */
export interface ReferenceChoice {
  /** Identifies the media itself, so the same file is offered once. */
  id: string;
  kind: ReferenceChoiceKind;
  url: string;
  label: string;
  /** Where it comes from: Generated, Sketch, Reference, a character's name, Earlier take. */
  note: string;
  mimeType?: string;
  durationSec?: number;
  sizeBytes?: number;
  width?: number;
  height?: number;
  /** The Reference node holding it, when the list offers Reference nodes. */
  nodeId?: string;
  tag?: ReferenceTag;
  /** Set for a Reference node in another workspace of the same project, which is used from there. */
  workspaceId?: string;
  workspaceName?: string;
}

/**
 * The workspace's Reference nodes that hold something, newest first: what Image and Video
 * nodes can use as references without a connection. Each is offered under its name and tag.
 * With `from`, they are another workspace's (of the same project), offered to use from there.
 */
export const collectReferenceNodes = (nodes: CanvasNode[], from?: { workspaceId: string; workspaceName: string }): ReferenceChoice[] => {
  const choices: ReferenceChoice[] = [];
  for (const node of [...nodes].reverse()) {
    if (!isReferenceNode(node)) continue;
    const data = node.data;
    const kind = inferReferenceKind(data);
    if (!kind) continue;
    const base = {
      id: from ? `shared:${from.workspaceId}:${node.id}` : `node:${node.id}`,
      kind, nodeId: node.id, label: data.title || 'Reference', note: KIND_NOTE[kind],
      ...(data.referenceTag ? { tag: data.referenceTag } : {}),
      ...(from ? { workspaceId: from.workspaceId, workspaceName: from.workspaceName } : {}),
    };
    if (kind === 'image') choices.push({ ...base, url: data.imageUrl!, width: data.originalWidth, height: data.originalHeight });
    else if (kind === 'video') choices.push({ ...base, url: data.videoUri!, mimeType: data.videoMimeType, durationSec: data.videoDurationSec, sizeBytes: data.videoSizeBytes });
    else choices.push({ ...base, url: data.audioUrl!, mimeType: data.audioMimeType, durationSec: data.audioDurationSec, sizeBytes: data.audioSizeBytes });
  }
  return choices;
};

const KIND_NOTE: Record<ReferenceChoiceKind, string> = { image: 'Image', video: 'Video', audio: 'Audio' };

const ENTITY_TYPES = [NodeType.CHARACTER_NODE, NodeType.PROP_NODE, NodeType.LOCATION_NODE];

/** A node's own title, unless it is still the default one; then the start of its prompt. */
const DEFAULT_TITLES = new Set(['Gen Image', 'Image', 'Video', 'Untitled']);
const generatedLabel = (title: string | undefined, prompt: string | undefined, fallback: string): string =>
  title && !DEFAULT_TITLES.has(title) ? title : (prompt?.trim().slice(0, 40) || fallback);

/**
 * Everything in the workspace a Reference node could point at: pictures, videos and audio
 * that nodes hold now, newest node first, then earlier takes only the history still knows.
 * Addresses that die with the page (blob URLs) are left out, since a reference must last.
 */
export const collectReferenceChoices = (
  nodes: CanvasNode[],
  history: (HistoryItem | LightHistoryItem)[] = [],
): ReferenceChoice[] => {
  const choices: ReferenceChoice[] = [];
  const seen = new Set<string>();
  const add = (kind: ReferenceChoiceKind, url: string | undefined, fallbackKey: string, choice: Omit<ReferenceChoice, 'id' | 'kind' | 'url'>) => {
    if (!url || /^blob:/i.test(url) || isLocalMediaUrl(url)) return;
    const id = `${kind}:${stableMediaKey(url) ?? fallbackKey}`;
    if (seen.has(id)) return;
    seen.add(id);
    choices.push({ id, kind, url, ...choice });
  };

  for (const node of [...nodes].reverse()) {
    const data = node.data;
    const fallback = `node-${node.id}`;
    if (node.type === NodeType.IMAGE_GENERATOR) {
      // A node that made several images at once offers each of them
      const images: (string | undefined)[] = data.generatedImages?.length ? data.generatedImages : [data.generatedImageUri];
      const label = generatedLabel(data.title, data.prompt, 'Generated image');
      images.forEach((url, index) => add('image', url, `${fallback}-${index}`, { label, note: images.length > 1 ? `Generated ${index + 1} of ${images.length}` : 'Generated' }));
    } else if (node.type === NodeType.IMAGE_VIEWER) {
      add('image', data.imageUrl, fallback, { label: data.title || 'Image', note: 'Image', width: data.originalWidth, height: data.originalHeight });
      add('video', data.videoUri, fallback, { label: data.title || 'Video', note: 'Video' });
    } else if (node.type === NodeType.SKETCH_PAD) {
      add('image', data.imageUrl, fallback, { label: data.title || 'Sketch', note: 'Sketch' });
    } else if (node.type === NodeType.VIDEO_GENERATOR) {
      add('video', data.videoUri, fallback, { label: generatedLabel(data.title, data.prompt, 'Generated video'), note: 'Generated', durationSec: data.videoDurationSec });
    } else if (isReferenceNode(node)) {
      add('image', data.imageUrl, fallback, { label: data.title || 'Reference image', note: 'Reference', width: data.originalWidth, height: data.originalHeight });
      add('video', data.videoUri, fallback, {
        label: data.videoFileName || data.title || 'Reference clip', note: 'Reference',
        mimeType: data.videoMimeType, durationSec: data.videoDurationSec, sizeBytes: data.videoSizeBytes,
      });
      add('audio', data.audioUrl, fallback, {
        label: data.audioFileName || data.title || 'Audio', note: 'Reference',
        mimeType: data.audioMimeType, durationSec: data.audioDurationSec, sizeBytes: data.audioSizeBytes,
      });
    } else if (ENTITY_TYPES.includes(node.type)) {
      const name = data.entityData?.name || 'Entity';
      (data.referenceImages || (data.imageUrl ? [data.imageUrl] : [])).forEach((url, index) => {
        add('image', url, `${fallback}-${index}`, { label: name, note: 'Entity' });
      });
    }
  }

  // Earlier takes can only be offered from a stored file
  const older = [...history].sort((a, b) => b.timestamp - a.timestamp);
  for (const item of older) {
    const full = 'data' in item ? item.data : undefined;
    const light = item as LightHistoryItem;
    const title = full ? full.title : light.title;
    const prompt = full ? full.prompt : light.prompt;
    if (item.type === NodeType.IMAGE_GENERATOR) {
      const url = full ? full.generatedImageUri : light.imageRef;
      if (url && stableMediaKey(url)) add('image', url, '', { label: generatedLabel(title, prompt, 'Generated image'), note: 'Earlier take' });
    } else if (item.type === NodeType.VIDEO_GENERATOR) {
      const url = full ? full.videoUri : light.videoRef;
      if (url && stableMediaKey(url)) add('video', url, '', { label: generatedLabel(title, prompt, 'Generated video'), note: 'Earlier take' });
    }
  }
  return choices;
};

/** Every audio file held by the workspace's Reference nodes. */
export const collectWorkspaceAudio = (nodes: CanvasNode[]): WorkspaceMediaItem[] => {
  const items: WorkspaceMediaItem[] = [];
  const seen = new Set<string>();
  for (const node of nodes) {
    const url = node.data.audioUrl;
    if (!url || !isReferenceNode(node)) continue;
    const key = stableMediaKey(url) ?? `node-${node.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ id: key, sourceId: key, url, label: node.data.audioFileName || node.data.title || 'Audio' });
  }
  return items;
};
